import {
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Icon,
    useTheme,
} from "@mui/material";
import { LngLatLike } from "mapbox-gl";
import { useContext, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AccountSubscriptionType, MapProjectItem } from "../../../../api/psc";
import { PageWithFilter } from "../../../../components/exports";
import {
    ClusterMaxZoom,
    ClusterRadius,
    GetClusterLayer,
    GetClusterPointsLayer,
    GetUnClusterPointsLayer,
    MapCenter,
    MapDefaultZoom,
    MapIcons,
    SourceId,
    UnClusteredPointId,
} from "../../../../constants/MapConstants";
import {
    DefaultDarkMapStyles,
    DefaultMapStyles,
} from "../../../../constants/ProjectConstants";
import { DialogContext } from "../../../../contexts/DialogContext";
import { RootState } from "../../../../slices/Store";
import ProjectDetailDialog from "../../../project-detail/ProjectDetailDialog";
import MapFilter from "../../components/MapFilter";
import { FetchPoints } from "../../slice/MapSlice";

// updated
export default function ProjectMap() {
    const dispatch = useDispatch();
    const theme = useTheme();
    const mapContainer = useRef(null);
    const map = useRef(null);
    const mapState = useSelector((r: RootState) => r.map);
    const { openDialog } = useContext(DialogContext);

    const fetch = () => {
        FetchPoints(mapState, dispatch);
    };

    useEffect(() => {
        fetch();
    }, [mapState.id]);

    useEffect(() => {
        const style =
            theme.palette.mode === "light"
                ? DefaultMapStyles
                : DefaultDarkMapStyles;

        if (!map.current) {
            import("mapbox-gl").then((mapboxgl) => {
                try {
                    mapboxgl.setRTLTextPlugin(
                        process.env.REACT_APP_MAP_STYLE_URL,
                        null
                    );
                } catch {}

                map.current = new mapboxgl.Map({
                    container: mapContainer.current,
                    style: style,
                    center: MapCenter as LngLatLike,
                    zoom: MapDefaultZoom,
                });
            });
        }

        if (map.current?.loaded()) {
            setupMap();
        } else {
            if (map.current) {
                map.current.on("load", () => {
                    setupMap();
                });
            }
        }
    }, [mapState.points]);

    const openFilterDialog = () => {
        openDialog(<MapFilter />);
    };

    const getMapIconForProject = (point: MapProjectItem) => {
        if (point.isVisited) {
            return 1;
        }
        if (point.requiredSubscriptionType === undefined) {
            return 2;
        }
        switch (point.requiredSubscriptionType) {
            case AccountSubscriptionType.STANDARD:
                return 4;
            case AccountSubscriptionType.ENHANCED:
                return 5;
            case AccountSubscriptionType.ULTIMATE:
                return 6;
            case AccountSubscriptionType.ULTIMATE_PLUS:
                return 7;
        }
    };

    const getDataForPoint = (point: MapProjectItem) => {
        return {
            type: "Feature",
            properties: {
                status: getMapIconForProject(point),
                hashId: point.hashId,
            },
            geometry: {
                type: "Point",
                coordinates: [point.longitude, point.latitude],
            },
        };
    };

    const setupMap = () => {
        const source = map.current.getSource(SourceId);

        if (!mapState.points) {
            return;
        }

        const data = mapState.points.map((l) => getDataForPoint(l));

        // if source and layers aren't setup
        if (!source) {
            map.current.addSource(SourceId, {
                type: "geojson",
                cluster: true,
                clusterMaxZoom: ClusterMaxZoom, // Max zoom to cluster points on
                clusterRadius: ClusterRadius,
                data: {
                    type: "FeatureCollection",
                    features: data,
                },
            });

            map.current.addLayer(GetClusterLayer);
            map.current.addLayer(GetClusterPointsLayer);
            map.current.addLayer(GetUnClusterPointsLayer);

            for (const icon of MapIcons) {
                map.current.loadImage(icon.src, (err, img) => {
                    map.current.addImage(icon.id, img);
                });
            }

            map.current.on("click", UnClusteredPointId, (e) => {
                const coordinates = e.features[0];
                const hashId = coordinates.properties.hashId;
                openDialog(<ProjectDetailDialog projectHashId={hashId} />);
            });
        } else {
            source.setData({
                type: "FeatureCollection",
                features: data,
            });
        }
    };

    return (
        <Box sx={{ height: "100%" }}>
            <Box sx={{ height: "100%" }}>
                <PageWithFilter filter={<MapFilter />}>
                    <Box
                        sx={{ width: "100%", height: "100%" }}
                        ref={mapContainer}
                        className="map-container"
                    />

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={openFilterDialog}
                        startIcon={<Icon>filter_alt</Icon>}
                        sx={{
                            position: "fixed",
                            bottom: 16,
                            right: 16,
                            zIndex: 1,
                            display: { xs: "flex", lg: "none" },
                        }}
                    >
                        مشاهده فیلتر ها
                    </Button>
                </PageWithFilter>
            </Box>

            <Backdrop open={mapState.isLoading}>
                <CircularProgress sx={{ color: "white" }} />
            </Backdrop>
        </Box>
    );
}
